import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  FileText,
  PenTool,
  CheckCircle,
  Users,
  Building,
  DollarSign,
  AlertCircle,
  Loader2,
  RotateCcw,
  User,
} from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
};

const SignProposalPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const sigCanvas = useRef(null);
  
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [printedName, setPrintedName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [signed, setSigned] = useState(false);
  const [signedData, setSignedData] = useState(null);

  useEffect(() => {
    const fetchProposal = async () => {
      try {
        // Use public endpoint (no auth required)
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sign/${token}`);
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to load proposal');
        }
        const data = await response.json();
        setProposal(data);
        
        // Pre-fill printed name with primary owner name
        if (data.primary_owner_name) {
          setPrintedName(data.primary_owner_name);
        }
      } catch (err) {
        console.error('Failed to load proposal:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchProposal();
    }
  }, [token]);

  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
  };

  const handleSubmit = async () => {
    if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
      toast.error('Please draw your signature');
      return;
    }

    if (!printedName.trim()) {
      toast.error('Please enter your printed name');
      return;
    }

    setSubmitting(true);
    try {
      const signatureImage = sigCanvas.current.toDataURL('image/png');
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sign/${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          signature_image: signatureImage,
          printed_name: printedName.trim(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to submit signature');
      }

      const data = await response.json();
      setSigned(true);
      setSignedData(data);
      toast.success('Proposal signed successfully!');
    } catch (err) {
      console.error('Failed to submit signature:', err);
      toast.error(err.message || 'Failed to submit signature');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-slate-600">Loading proposal...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">Unable to Load Proposal</h2>
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (signed) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Signature Submitted!</h2>
            <p className="text-slate-600 mb-4">
              Thank you for signing. Your proposal has been recorded.
            </p>
            <div className="bg-slate-50 rounded-lg p-4 text-left text-sm">
              <p><strong>Deal:</strong> {signedData?.deal_number}</p>
              <p><strong>Signed by:</strong> {signedData?.signed_by}</p>
              <p><strong>Date:</strong> {new Date(signedData?.signed_at).toLocaleString()}</p>
            </div>
            <p className="text-sm text-slate-500 mt-4">
              You may close this window. A copy will be sent to your email.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-primary">Prestigious Travel Club</h1>
          <p className="text-slate-600">Timeshare Exit Agreement</p>
        </div>

        {/* Proposal Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Proposal Review - {proposal?.deal_number}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Owner Information */}
            <div>
              <h3 className="font-semibold mb-2 flex items-center gap-2">
                <Users className="h-4 w-4" />
                Owner Information ({proposal?.owners?.length || 0} Owner{proposal?.owners?.length !== 1 ? 's' : ''})
              </h3>
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                {proposal?.owners?.map((owner, idx) => (
                  <div key={`${owner.first_name}-${owner.last_name}-${idx}`} className="text-sm">
                    <span className="font-medium">{idx === 0 ? 'Primary Owner' : `Owner ${idx + 1}`}:</span>{' '}
                    {owner.first_name} {owner.last_name}
                    {owner.email && <span className="text-slate-500"> ({owner.email})</span>}
                  </div>
                ))}
              </div>
            </div>

            {/* Timeshare Information */}
            {proposal?.timeshares?.length > 0 && (
              <div>
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <Building className="h-4 w-4" />
                  Timeshare Information ({proposal.timeshares.length} Property{proposal.timeshares.length !== 1 ? 'ies' : ''})
                </h3>
                <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                  {proposal.timeshares.map((ts, idx) => (
                    <div key={ts.resort_name || `ts-${idx}`} className="text-sm border-b last:border-0 pb-2 last:pb-0">
                      <p className="font-medium">{ts.resort_name}</p>
                      <p className="text-slate-500">{ts.resort_city}, {ts.resort_state}</p>
                      {ts.balance_owed > 0 && (
                        <p className="text-amber-600">Balance Owed: {formatCurrency(ts.balance_owed)}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pricing */}
            <div>
              <h3 className="font-semibold mb-2 flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Agreement Pricing
              </h3>
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Service Fee:</span>
                    <span>{formatCurrency(proposal?.price)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Internal Transfer Fee:</span>
                    <span>{formatCurrency(proposal?.internal_transfer_fee)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>External Transfer Fee:</span>
                    <span>{formatCurrency(proposal?.external_transfer_fee)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Estoppel Fee:</span>
                    <span>{formatCurrency(proposal?.estoppel_fee)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Court Cost:</span>
                    <span>
                      {formatCurrency(proposal?.court_cost)}
                      {proposal?.court_county && (
                        <span className="text-xs text-slate-500 ml-1">({proposal.court_county})</span>
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between pt-2 border-t font-bold text-lg">
                    <span>Total:</span>
                    <span className="text-primary">{formatCurrency(proposal?.total_deal_price)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Sales Representative */}
            {proposal?.advisor_name && (
              <div>
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Your Representative
                </h3>
                <div className="bg-slate-50 rounded-lg p-4 text-sm">
                  <p><strong>Advisor:</strong> {proposal.advisor_name}</p>
                  {proposal.manager_name && <p><strong>Manager:</strong> {proposal.manager_name}</p>}
                  {proposal.director_name && <p><strong>Director:</strong> {proposal.director_name}</p>}
                </div>
              </div>
            )}

            {/* Notes */}
            {proposal?.notes && (
              <div>
                <h3 className="font-semibold mb-2">Additional Notes</h3>
                <p className="text-sm text-slate-600 bg-slate-50 rounded-lg p-4">{proposal.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Signature Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PenTool className="h-5 w-5" />
              Sign Agreement
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                By signing below, you agree to the terms and conditions of this Timeshare Exit Agreement.
                Please review all details above before signing.
              </p>
            </div>

            {/* Signature Pad */}
            <div>
              <Label>Your Signature *</Label>
              <div className="border-2 border-dashed border-slate-300 rounded-lg mt-2 bg-white">
                <SignatureCanvas
                  ref={sigCanvas}
                  canvasProps={{
                    className: 'w-full h-40',
                    style: { width: '100%', height: '160px' }
                  }}
                  backgroundColor="white"
                />
              </div>
              <div className="flex justify-end mt-2">
                <Button variant="outline" size="sm" onClick={clearSignature}>
                  <RotateCcw className="h-3 w-3 mr-1" />
                  Clear Signature
                </Button>
              </div>
            </div>

            {/* Printed Name */}
            <div>
              <Label>Print Your Full Name *</Label>
              <Input
                value={printedName}
                onChange={(e) => setPrintedName(e.target.value)}
                placeholder="Enter your full legal name"
                className="mt-2"
                data-testid="printed-name-input"
              />
            </div>

            {/* Submit Button */}
            <div className="pt-4 border-t">
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full bg-green-600 hover:bg-green-700"
                size="lg"
                data-testid="submit-signature-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Sign & Submit Agreement
                  </>
                )}
              </Button>
              <p className="text-xs text-slate-500 text-center mt-2">
                Your signature and submission will be recorded with timestamp and IP address for legal purposes.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center py-6 mt-8 border-t border-slate-200">
          <p className="text-sm text-slate-600 font-medium">
            © 2026 PTC Client Hub + EasyQuickFund
          </p>
          <p className="text-xs text-slate-500 mt-1">
            A Prestigious Companies Network | Strategic Partner of the Prestigious Travel Club Foundation
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignProposalPage;
